import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import { verifyCode, markCodeAsUsed, createVerificationCode } from '../utils/smsVerificationAPI';
import { loginWithPassword, checkUserExists, saveUserLoginState, createExperienceUser } from '../utils/profilesAPI';

function Login() {
  const [loading, setLoading] = useState(false);
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [loginType, setLoginType] = useState('password'); // 'password' 或 'code'
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [codeLoading, setCodeLoading] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  // 检查是否有来自注册页面的消息
  useEffect(() => {
    if (location.state?.message) {
      setMessage(location.state.message);
      // 清除state避免重新加载页面时再次显示
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  /**
   * 对密码进行哈希处理
   * @param {string} password 用户输入的密码
   * @returns {Promise<string>} 密码的哈希值
   */
  const hashPassword = async (password) => {
    // 使用 SHA-256 哈希密码
    const encoder = new TextEncoder();
    const data = encoder.encode(password);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
  };

  /**
   * 发送验证码
   */
  const handleSendCode = async () => {
    if (!/^1[3-9]\d{9}$/.test(phone)) {
      setError('请输入有效的手机号');
      return;
    }
    
    try {
      setCodeLoading(true);
      setError(null);
      
      const result = await createVerificationCode(phone);
      
      if (!result.success) {
        throw new Error(result.error);
      }
      
      setMessage(`验证码已发送`);
      setRemainingTime(60);
      
      // 开始倒计时
      const interval = setInterval(() => {
        setRemainingTime((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      setError(error.message || '发送验证码失败，请重试');
    } finally {
      setCodeLoading(false);
    }
  };

  /**
   * 处理密码登录
   */
  const handlePasswordLogin = async () => {
    if (!/^1[3-9]\d{9}$/.test(phone)) {
      setError('请输入有效的手机号');
      return;
    }
    
    if (!password) {
      setError('请输入密码');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // 1. 计算输入密码的哈希值
      const hashedPassword = await hashPassword(password);
      
      // 2. 调用登录API
      const result = await loginWithPassword(phone, hashedPassword);
      
      if (!result.success) {
        throw new Error(result.error);
      }
      
      // 3. 登录成功，保存用户状态
      saveUserLoginState(result.user);
      
      // 4. 跳转到首页
      navigate('/');
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * 处理验证码登录
   */
  const handleCodeLogin = async () => {
    if (!/^1[3-9]\d{9}$/.test(phone)) {
      setError('请输入有效的手机号');
      return;
    }
    
    if (!code) {
      setError('请输入验证码');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // 1. 校验验证码
      const verifyResult = await verifyCode(phone, code);

      if (!verifyResult.success) {
        throw new Error(verifyResult.error);
      }
      
      // 2. 查找用户是否存在
      const userResult = await checkUserExists(phone);
      
      if (!userResult.success) {
        // 如果错误是用户不存在，导航到注册页面
        if (!userResult.userExists) {
          setError('该手机号未注册，请先注册');
          navigate('/register', { state: { phone } });
          return;
        }
        throw new Error(userResult.error);
      }
      
      // 3. 将验证码标记为已使用
      await markCodeAsUsed(verifyResult.data.id);
      
      // 4. 登录成功，保存用户状态
      saveUserLoginState(userResult.user);
      
      // 5. 跳转到首页
      navigate('/');
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (loginType === 'password') {
      handlePasswordLogin();
    } else {
      handleCodeLogin();
    }
  };

  // 切换登录方式
  const toggleLoginType = () => {
    setLoginType(prev => prev === 'password' ? 'code' : 'password');
    setError(null); // 清除错误信息
  };

  // 为简化演示，保留快速登录功能
  const handleQuickLogin = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // 使用快速登录API
      const result = await createExperienceUser();
      
      if (!result.success) {
        throw new Error(result.error);
      }
      
      // 跳转到首页
      navigate('/');
    } catch (error) {
      setError('登录失败，请重试');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Header title="登录" showBack={false} />
      
      <div className="flex-1 p-6 flex flex-col justify-center">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-2">欢迎回来</h2>
          <p className="text-gray-600">登录您的账号继续使用</p>
        </div>
        
        {/* 登录方式选择 */}
        <div className="flex justify-center mb-6">
          <div className="flex bg-gray-100 p-1 rounded-lg">
            <button
              className={`px-4 py-2 rounded-md text-sm ${
                loginType === 'password' 
                ? 'bg-white text-primary shadow-sm' 
                : 'text-gray-600'
              }`}
              onClick={() => setLoginType('password')}
            >
              密码登录
            </button>
            <button
              className={`px-4 py-2 rounded-md text-sm ${
                loginType === 'code' 
                ? 'bg-white text-primary shadow-sm' 
                : 'text-gray-600'
              }`}
              onClick={() => setLoginType('code')}
            >
              验证码登录
            </button>
          </div>
        </div>
        
        {message && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            {message}
          </div>
        )}
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}
        
        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
              手机号
            </label>
            <input
              id="phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-light"
              required
              maxLength={11}
              placeholder="请输入手机号"
            />
          </div>
          
          {loginType === 'password' ? (
            <div>
              <div className="flex justify-between items-center mb-1">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  密码
                </label>
                <button 
                  type="button"
                  onClick={toggleLoginType}
                  className="text-xs text-primary"
                >
                  验证码登录
                </button>
              </div>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-light"
                required
                placeholder="请输入密码"
              />
            </div>
          ) : (
            <div>
              <div className="flex justify-between items-center mb-1">
                <label htmlFor="code" className="block text-sm font-medium text-gray-700">
                  验证码
                </label>
                <button 
                  type="button"
                  onClick={toggleLoginType}
                  className="text-xs text-primary"
                >
                  密码登录
                </button>
              </div>
              <div className="flex items-center gap-2">
                <input
                  id="code"
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="flex-1 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-light"
                  required
                  maxLength={6}
                  placeholder="请输入验证码"
                />
                <button
                  type="button"
                  className={`px-4 h-12 rounded-lg text-white font-medium text-sm ${
                    remainingTime > 0 || codeLoading 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-primary hover:bg-primary-dark'
                  }`}
                  onClick={handleSendCode}
                  disabled={remainingTime > 0 || codeLoading}
                >
                  {codeLoading ? '发送中' : remainingTime > 0 ? `${remainingTime}秒` : '获取验证码'}
                </button>
              </div>
            </div>
          )}
          
          <button
            type="submit"
            className="w-full bg-gradient-to-r from-primary-dark to-primary py-3 rounded-lg text-white font-semibold shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all"
            disabled={loading}
          >
            {loading ? '登录中...' : '登录'}
          </button>
        </form>
        
        <button
          onClick={handleQuickLogin}
          className="w-full mt-4 bg-green-600 py-3 rounded-lg text-white font-semibold shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all"
        >
          快速体验（无需账号）
        </button>
        
        <div className="mt-6 text-center">
          <p className="text-gray-600">
            还没有账号？{' '}
            <Link to="/register" className="text-primary-dark font-semibold hover:underline">
              立即注册
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login; 